import { Fragment, useEffect, useRef, useState } from "react"
import BarcodeScannerComponent from "react-qr-barcode-scanner"
import {
  BottomCtaBox,
  GalleryButtonWrapper,
  CloseButton,
  EnterManuallyButtonWrapper,
  EnterManuallyText,
  HeaderSection,
  HeaderContent,
  HeaderTitle,
  HeaderSubtitle,
  HeaderSubtitleText,
  HeaderBadge,
  HeaderImage,
} from "./styled"
import BarcodeManuallyIcon from "components/Icons/BarcodeManuallyIcon"
import GalleryIcon from "components/Icons/GalleryIcon"
import useSearchFoodByBarcode from "features/meal/hooks/useSearchFoodByBarcode"
import LoadingCenter from "components/loading/LoadingCenter"
import PopConfirm from "components/PopConfirm"
import BarcodeInput from "components/inputs/BarcodeInput"
import CloseWhiteIcon from "components/Icons/CloseWhiteIcon"
import barcodeImage from "assets/images/barcode-scan.png"
import Overlay from "./Overlay"
import { useProcessImage } from "features/image/hooks"
import {
  convertImageUrlToFile,
  createImageChangeHandler,
} from "utils/imageConverter"
import { useAppDispatch } from "app/hooks"
import { resetImageState } from "features/image/imageSlice"
import { BrowserMultiFormatReader } from "@zxing/browser"
import HiddenImageInput from "components/inputs/ImageUploader/HiddenImageInput"
import { isDigitsOnly } from "utils"
import { isIOSWebView } from "utils/webViewDetection"

const reader = new BrowserMultiFormatReader()

const EnterManuallyButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <EnterManuallyButtonWrapper onClick={onClick}>
      <BarcodeManuallyIcon />
      <EnterManuallyText> {"Enter Barcode Manually"}</EnterManuallyText>
    </EnterManuallyButtonWrapper>
  )
}

const GalleryButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <GalleryButtonWrapper onClick={onClick}>
      <GalleryIcon />
    </GalleryButtonWrapper>
  )
}

const BarcodeScanning = ({
  videoConstraints,
  cameraResetKey,
  onCameraError,
  goToScanResult,
  goToSearch,
  onClose,
}: {
  videoConstraints: MediaTrackConstraints | null
  cameraResetKey: number
  onCameraError: (error: string | DOMException) => void
  goToScanResult: () => void
  goToSearch: () => void
  onClose: () => void
}) => {
  const dispatch = useAppDispatch()
  const [isDisableScanning, setIsDisableScanning] = useState(false)
  const [stopStream, setStopStream] = useState(false)
  const [isProcessingImageFile, setIsProcessingImageFile] = useState(false)
  const [isNoFoodFoundPopConfirmOpen, setIsNoFoodFoundPopConfirmOpen] =
    useState(false)
  const [isBarcodeManuallyOpen, setIsBarcodeManuallyOpen] = useState(false)
  const [barcode, setBarcode] = useState("")

  const imageInputRef = useRef<HTMLInputElement>(null)

  const { imageUrl, processImageLoading, handleProcessImage } =
    useProcessImage()

  const onBarcodeChanged = (value: string) => {
    setBarcode(value)
  }

  const {
    handleSearchFoodByBarcode,
    searchFoodByBarcodeLoading,
    noFoodFoundFromBarcode,
    clearNoFoodFromBarcodeState,
  } = useSearchFoodByBarcode(goToScanResult)

  const scanBarcode = (barcode: string) => {
    handleSearchFoodByBarcode(barcode)
    setIsDisableScanning(true)
  }

  const videoContainerRef = useRef<HTMLDivElement>(null)

  // Detect the first rendered video frame via the native "playing" event
  // instead of guessing with a fixed delay.
  useEffect(() => {
    const container = videoContainerRef.current
    if (!container) return

    let observedVideo: HTMLVideoElement | null = null

    const handlePlaying = () => {
      const delay = isIOSWebView() ? 60 : 0
      setTimeout(() => {
        setIsVideoReady(true)
      }, delay)
    }

    const attachToVideo = (video: HTMLVideoElement) => {
      observedVideo = video
      video.addEventListener("playing", handlePlaying, { once: true })
    }

    const existingVideo = container.querySelector("video")
    if (existingVideo) {
      attachToVideo(existingVideo)
      return () => {
        observedVideo?.removeEventListener("playing", handlePlaying)
      }
    }

    const observer = new MutationObserver(() => {
      const video = container.querySelector("video")
      if (video) {
        observer.disconnect()
        attachToVideo(video)
      }
    })
    observer.observe(container, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      observedVideo?.removeEventListener("playing", handlePlaying)
    }
  }, [])

  // Handle standard image files (JPG, PNG, etc.)
  const onUploadImage = (_: string, file: File) => {
    if (!file) return
    setIsDisableScanning(true)
    setIsProcessingImageFile(true)

    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = async () => {
      try {
        const result = await reader.decodeFromImageElement(img)
        const code = result.getText()
        if (isDigitsOnly(code)) {
          scanBarcode(code)
        } else {
          setIsNoFoodFoundPopConfirmOpen(true)
        }
      } catch (err) {
        console.error(err)
        setIsNoFoodFoundPopConfirmOpen(true)
      } finally {
        setTimeout(() => {
          setIsProcessingImageFile(false)
        }, 200)
      }
    }

    img.onerror = () => {
      setIsNoFoodFoundPopConfirmOpen(true)
      setIsProcessingImageFile(false)
    }

    img.src = url
  }

  // Handle HEIC/HEIF files that need conversion
  const onProcessImage = (file: File) => {
    setIsDisableScanning(true)
    handleProcessImage(file)
    setIsProcessingImageFile(true)
  }

  const handleImageChange = createImageChangeHandler({
    onProcessImage,
    onUploadImage,
  })

  // Watch for processed HEIC/HEIF image URL
  useEffect(() => {
    if (imageUrl && processImageLoading === false) {
      const processImage = async () => {
        try {
          const file = await convertImageUrlToFile(imageUrl)
          onUploadImage("", file)
        } catch (error) {
          console.error("Failed to convert processed image:", error)
          setIsNoFoodFoundPopConfirmOpen(true)
          setIsProcessingImageFile(false)
        }
      }
      processImage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, processImageLoading])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setStopStream(true)
      clearNoFoodFromBarcodeState()
      dispatch(resetImageState())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (noFoodFoundFromBarcode) {
      setIsDisableScanning(true)
      setIsNoFoodFoundPopConfirmOpen(true)
    }
  }, [noFoodFoundFromBarcode])

  const isManualBarcodeValid = isDigitsOnly(barcode)

  const isDisableScanningRef = useRef(isDisableScanning)
  const isSearchFoodByBarcodeLoadingRef = useRef(searchFoodByBarcodeLoading)

  useEffect(() => {
    isDisableScanningRef.current = isDisableScanning
    isSearchFoodByBarcodeLoadingRef.current = searchFoodByBarcodeLoading
  }, [isDisableScanning, searchFoodByBarcodeLoading])

  const isLoading =
    searchFoodByBarcodeLoading || processImageLoading || isProcessingImageFile
  const isShowCenterLoading = isLoading && !isBarcodeManuallyOpen

  const [isVideoReady, setIsVideoReady] = useState(false)

  const handleBarcodeUpdate = (err: unknown, result: any) => {
    const hasScanError = Boolean(err)
    const isBarcodeScanBusy =
      isDisableScanningRef.current || isSearchFoodByBarcodeLoadingRef.current

    if (hasScanError || isBarcodeScanBusy) {
      return
    }

    if (result) {
      const code = result.text
      if (isDigitsOnly(code)) {
        scanBarcode(code)
        setIsDisableScanning(true)
      }
    }
  }

  return (
    <Fragment>
      <HeaderSection>
        <HeaderContent>
          <HeaderTitle>Barcode</HeaderTitle>
          <HeaderSubtitle>
            <HeaderSubtitleText>Easily log by scanning a</HeaderSubtitleText>
            <HeaderBadge>barcode</HeaderBadge>
          </HeaderSubtitle>
        </HeaderContent>
        <HeaderImage>
          <img src={barcodeImage} alt="Product example" />
        </HeaderImage>
      </HeaderSection>

      <div
        ref={videoContainerRef}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          opacity: isVideoReady ? 1 : 0.1,
        }}
      >
        {videoConstraints && (
          <BarcodeScannerComponent
            key={cameraResetKey}
            width={"100%"}
            height={"100%"}
            stopStream={stopStream}
            onUpdate={handleBarcodeUpdate}
            onError={onCameraError}
            videoConstraints={videoConstraints}
          />
        )}
      </div>

      <Overlay isVideoReady={isVideoReady} />

      <div id="barcode-file-reader" style={{ display: "none" }} />

      <BottomCtaBox>
        <GalleryButton
          onClick={() => {
            if (isNoFoodFoundPopConfirmOpen || isDisableScanning) {
              return
            }
            imageInputRef.current?.click()
          }}
        />
        <EnterManuallyButton
          onClick={() => {
            if (isNoFoodFoundPopConfirmOpen) {
              return
            }
            setIsBarcodeManuallyOpen(true)
            setIsDisableScanning(true)
          }}
        />
      </BottomCtaBox>

      {/* Hidden image input for gallery selection */}
      <HiddenImageInput
        ref={imageInputRef}
        onChange={(file) => {
          handleImageChange(file)
        }}
      />

      {isShowCenterLoading && <LoadingCenter />}

      <PopConfirm
        loading={false}
        isOpen={isNoFoodFoundPopConfirmOpen}
        title="Barcode not found"
        onConfirm={() => {
          goToSearch()
          setIsNoFoodFoundPopConfirmOpen(false)
        }}
        onCancel={() => {
          setIsNoFoodFoundPopConfirmOpen(false)
          setIsDisableScanning(false)
          clearNoFoodFromBarcodeState()
        }}
        cancelText="Retry"
        confirmText="Go to Search"
      >
        <div style={{ textAlign: "center" }}>
          We couldn’t find a match for this barcode. Please try again or search
          for it manually.
        </div>
      </PopConfirm>

      <PopConfirm
        type="modal"
        disabled={!isManualBarcodeValid}
        loading={searchFoodByBarcodeLoading}
        isOpen={isBarcodeManuallyOpen && !isNoFoodFoundPopConfirmOpen}
        title="Enter Barcode Manually"
        onConfirm={() => {
          if (isManualBarcodeValid) {
            scanBarcode(barcode)
          }
        }}
        onCancel={() => {
          setIsDisableScanning(false)
          setIsBarcodeManuallyOpen(false)
        }}
        cancelText="Cancel"
        confirmText="Submit"
      >
        <BarcodeInput
          label="Barcode"
          placeholder="Enter the barcode number"
          value={barcode}
          onChange={onBarcodeChanged}
          disabled={searchFoodByBarcodeLoading}
        />
      </PopConfirm>

      <CloseButton onClick={onClose}>
        <CloseWhiteIcon />
      </CloseButton>
    </Fragment>
  )
}

export default BarcodeScanning
